import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Modal } from '@/components/dialogs/Modal';
import { Tabs } from '@/components/common/Tabs';
import { TextField } from '@/components/forms/TextField';
import { Spinner } from '@/components/common/Spinner';
import type { IdentityRoleDto } from '@/api/types/identity';
import {
  MAX_EMAIL_LENGTH,
  MAX_NAME_LENGTH,
  MAX_PASSWORD_LENGTH,
  MAX_PHONE_LENGTH,
  MAX_SURNAME_LENGTH,
  MAX_USERNAME_LENGTH,
} from '@/api/types/identity';

export interface UserFormValues {
  userName: string;
  name: string;
  surname: string;
  email: string;
  phoneNumber: string;
  password: string;
  isActive: boolean;
  lockoutEnabled: boolean;
  isApiUser: boolean;
  roleNames: string[];
}

function makeSchema(mode: 'create' | 'edit') {
  return z.object({
    userName: z
      .string()
      .trim()
      .min(1, 'Please enter a username.')
      .max(MAX_USERNAME_LENGTH)
      .regex(/^[a-zA-Z0-9._@+\-]+$/, 'The username contains invalid characters.'),
    name: z.string().trim().max(MAX_NAME_LENGTH),
    surname: z.string().trim().max(MAX_SURNAME_LENGTH),
    email: z
      .string()
      .trim()
      .min(1, 'Please enter an email address.')
      .email('Please enter a valid email address.')
      .max(MAX_EMAIL_LENGTH),
    phoneNumber: z.string().trim().max(MAX_PHONE_LENGTH),
    // Required on create, optional (= keep current) on edit.
    password:
      mode === 'create'
        ? z.string().min(1, 'Please enter a password.').max(MAX_PASSWORD_LENGTH)
        : z.string().max(MAX_PASSWORD_LENGTH),
    isActive: z.boolean(),
    lockoutEnabled: z.boolean(),
    isApiUser: z.boolean(),
    roleNames: z.array(z.string()),
  });
}

interface UserFormModalProps {
  show: boolean;
  mode: 'create' | 'edit';
  loading?: boolean;
  roles: IdentityRoleDto[];
  // Whether IsActive may be toggled (legacy hides it when editing the current user).
  canToggleActive?: boolean;
  initial?: Partial<UserFormValues>;
  busy?: boolean;
  onSubmit: (values: UserFormValues) => void;
  onClose: () => void;
}

type Tab = 'info' | 'roles';

export function UserFormModal({
  show,
  mode,
  loading = false,
  roles,
  canToggleActive = true,
  initial,
  busy = false,
  onSubmit,
  onClose,
}: UserFormModalProps) {
  const [tab, setTab] = useState<Tab>('info');
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<UserFormValues>({
    resolver: zodResolver(makeSchema(mode)),
    defaultValues: {
      userName: '',
      name: '',
      surname: '',
      email: '',
      phoneNumber: '',
      password: '',
      isActive: true,
      lockoutEnabled: true,
      isApiUser: false,
      roleNames: [],
    },
  });

  useEffect(() => {
    if (!show) return;
    setTab('info');
    setShowPassword(false);
    reset({
      userName: initial?.userName ?? '',
      name: initial?.name ?? '',
      surname: initial?.surname ?? '',
      email: initial?.email ?? '',
      phoneNumber: initial?.phoneNumber ?? '',
      password: '',
      isActive: initial?.isActive ?? true,
      lockoutEnabled: initial?.lockoutEnabled ?? true,
      isApiUser: initial?.isApiUser ?? false,
      roleNames: initial?.roleNames ?? [],
    });
  }, [show, initial, reset]);

  const selectedRoles = watch('roleNames');

  function toggleRole(name: string, checked: boolean) {
    const next = checked
      ? [...selectedRoles, name]
      : selectedRoles.filter((r) => r !== name);
    setValue('roleNames', next, { shouldDirty: true });
  }

  return (
    <Modal
      show={show}
      title={mode === 'create' ? 'New User' : 'Edit'}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-light" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" form="user-form" className="btn btn-primary" disabled={busy || loading}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      {loading ? (
        <Spinner />
      ) : (
        <form id="user-form" onSubmit={handleSubmit(onSubmit)} noValidate>
          <Tabs
            tabs={[
              { key: 'info', title: 'User Information' },
              { key: 'roles', title: 'Roles' },
            ]}
            active={tab}
            onChange={(k) => setTab(k as Tab)}
            className="mb-3"
          />

          <div className={tab === 'info' ? '' : 'd-none'}>
            <TextField label="User name" autoComplete="off" error={errors.userName?.message} {...register('userName')} />
            <TextField label="Name" autoComplete="off" error={errors.name?.message} {...register('name')} />
            <TextField label="Surname" autoComplete="off" error={errors.surname?.message} {...register('surname')} />

            <div className="mb-3">
              <label htmlFor="user-password" className="form-label">
                Password {mode === 'create' ? '*' : ''}
              </label>
              <div className="input-group">
                <input
                  id="user-password"
                  type={showPassword ? 'text' : 'password'}
                  className={`form-control${errors.password ? ' is-invalid' : ''}`}
                  autoComplete="new-password"
                  maxLength={MAX_PASSWORD_LENGTH}
                  {...register('password')}
                />
                <button
                  type="button"
                  className="btn btn-light"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label="Toggle password visibility"
                >
                  <i className={`fa ${showPassword ? 'fa-eye' : 'fa-eye-slash'}`} aria-hidden="true" />
                </button>
              </div>
              {errors.password && <div className="invalid-feedback d-block">{errors.password.message}</div>}
              {mode === 'edit' && (
                <div className="form-text">
                  <i className="fa fa-info-circle me-1" />
                  Leave blank to keep the current password.
                </div>
              )}
            </div>

            <TextField label="Email address" type="email" autoComplete="off" error={errors.email?.message} {...register('email')} />
            <TextField label="Phone Number" autoComplete="off" error={errors.phoneNumber?.message} {...register('phoneNumber')} />

            {canToggleActive && (
              <div className="form-check mb-3">
                <input id="user-is-active" type="checkbox" className="form-check-input" {...register('isActive')} />
                <label htmlFor="user-is-active" className="form-check-label">
                  Is Active
                </label>
              </div>
            )}

            <div className="form-check mb-3">
              <input id="user-lockout" type="checkbox" className="form-check-input" {...register('lockoutEnabled')} />
              <label htmlFor="user-lockout" className="form-check-label">
                Lockout Enabled
              </label>
            </div>

            <div className="form-check mb-3">
              <input id="user-is-api-user" type="checkbox" className="form-check-input" {...register('isApiUser')} />
              <label htmlFor="user-is-api-user" className="form-check-label">
                API user (API access only, no reCAPTCHA)
              </label>
            </div>
          </div>

          <div className={tab === 'roles' ? '' : 'd-none'}>
            {roles.length === 0 ? (
              <div className="text-muted">No assignable roles.</div>
            ) : (
              roles.map((role) => (
                <div className="form-check mb-2" key={role.id}>
                  <input
                    id={`role-${role.id}`}
                    type="checkbox"
                    className="form-check-input"
                    checked={selectedRoles.includes(role.name)}
                    onChange={(e) => toggleRole(role.name, e.target.checked)}
                  />
                  <label htmlFor={`role-${role.id}`} className="form-check-label">
                    {role.name}
                  </label>
                </div>
              ))
            )}
          </div>
        </form>
      )}
    </Modal>
  );
}
